import { Link } from "react-router-dom";
import { format } from "date-fns";
import { MapPin, Calendar, Users } from "lucide-react";
import { Card, CardHeader, CardContent, CardFooter } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import type { Trip } from "@/lib/types";
import { tripStatus } from "./tripStatusView";

export default function TripCard({ trip }: { trip: Trip }) {
  const allRoutes = trip.routes ?? [];
  // Show the bookable itinerary only — the primary outbound + return. Date/airport
  // VARIANTS (is_primary:false) are price-watch alternates, not extra legs; listing
  // them here made round trips look duplicated. Fall back to all routes only when
  // no row carries the flag (legacy trips created before leg tagging).
  const hasFlags = allRoutes.some((r) => r.is_primary !== undefined);
  const shownRoutes = hasFlags ? allRoutes.filter((r) => r.is_primary) : allRoutes;
  const watchedCount = allRoutes.length - shownRoutes.length;
  const status = tripStatus(trip);

  return (
    <Link to={"/trips/" + trip.id}>
      <Card
        className={
          "hover:border-primary/50 transition-colors cursor-pointer h-full" +
          (status.past ? " opacity-70" : "")
        }
      >
        <CardHeader className="pb-3">
          <div className="flex items-start justify-between">
            <h3 className="font-semibold text-lg leading-tight">{trip.name}</h3>
            <div className="flex flex-shrink-0 items-center gap-1.5">
              {trip.is_shared && (
                <Badge variant="outline" className="gap-1">
                  <Users className="h-3 w-3" />
                  {trip.owner_name ? `Shared by ${trip.owner_name.split(" ")[0]}` : "Shared"}
                </Badge>
              )}
              {/* "Past" WINS over the watch switch — see tripStatusView. A July
                  conference wore an "Active" badge into late August. */}
              <Badge variant={status.tone === "active" ? "default" : "secondary"} title={status.title}>
                {status.label}
              </Badge>
            </div>
          </div>
          {trip.description && (
            <p className="text-sm text-muted-foreground line-clamp-2">{trip.description}</p>
          )}
        </CardHeader>
        <CardContent className="pb-3">
          {shownRoutes.length > 0 ? (
            <div className="space-y-2">
              {shownRoutes.map((route) => (
                <div key={route.id} className="flex items-center gap-2 text-sm">
                  <MapPin className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />
                  <span className="font-mono font-medium">{route.origin}</span>
                  <span className="text-muted-foreground">→</span>
                  <span className="font-mono font-medium">{route.destination}</span>
                  <span className="font-mono text-muted-foreground ml-auto flex items-center gap-1">
                    <Calendar className="h-3 w-3" />
                    {route.departure_date}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">No routes defined</p>
          )}
        </CardContent>
        <CardFooter className="text-xs text-muted-foreground">
          <span>
            {shownRoutes.length} leg{shownRoutes.length !== 1 ? "s" : ""}
            {watchedCount > 0 ? ` · watching ${watchedCount} more` : ""}
          </span>
          <span className="ml-auto">
            Created {format(new Date(trip.created_at), "MMM d, yyyy")}
          </span>
        </CardFooter>
      </Card>
    </Link>
  );
}
